/**
 * The one place the grid talks to the server.
 *
 * TWO THINGS IT DOES THAT MATTER, and neither is about convenience:
 *
 * 1. It surfaces `insufficient_scope` as its own kind of failure. The server
 *    answers 403 with the scope it would have needed, and a UI that flattened
 *    that into "something went wrong" would turn a precise, actionable refusal
 *    into a support ticket. Matt's ruling makes scopes a real boundary; the
 *    person on the other side of it should be able to read it.
 *
 * 2. It never asks the server what a person may do. The grid asks what a person
 *    HAS — the scope set on /api/me — and hides the affordances they cannot use.
 *    That is a courtesy, not a control: every one of those actions is refused by
 *    the server and by the database whether or not the button is on screen.
 */

export interface ApiRefusal {
  kind: 'refused' | 'scope' | 'auth' | 'error';
  message: string;
  requiredScope?: string;
  /**
   * The HTTP status, carried so a caller can tell one 'error' from another
   * WITHOUT a new `kind` — added in C2-A because a 404 on one linked row is an
   * ordinary outcome (the row was trashed, or is not this person's to read) and
   * must not surface as an error banner, while every other failure still must.
   * `kind` stays the thing most callers switch on.
   */
  status?: number;
}

export class ApiError extends Error {
  readonly refusal: ApiRefusal;
  constructor(refusal: ApiRefusal) {
    super(refusal.message);
    this.refusal = refusal;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });

  if (res.ok) {
    return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
  }

  let body: { error?: string; message?: string; required_scope?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* an HTML error page, or nothing at all */
  }

  if (res.status === 401) {
    throw new ApiError({ kind: 'auth', message: body.message ?? 'Sign in to use the console.' });
  }
  if (body.error === 'insufficient_scope') {
    throw new ApiError({
      kind: 'scope',
      message: body.message ?? 'Your session does not carry the scope this needs.',
      requiredScope: body.required_scope,
    });
  }
  if (res.status === 403) {
    throw new ApiError({ kind: 'refused', message: body.message ?? 'That was refused.' });
  }
  throw new ApiError({
    kind: 'error',
    message: body.message ?? `Request failed (${res.status}).`,
    status: res.status,
  });
}

export type ClaudeSwitchRow = {
  switch: string;
  label: string;
  help: string;
  /** COMPUTED by the database. Never derived in the browser from enabled_at. */
  on: boolean;
  enabled_at: string | null;
  expires_at: string | null;
  duration_seconds: number;
  enabled_by_email: string | null;
};

export type ClaudeProposalRow = {
  id: string;
  kind: 'permission' | 'automation';
  summary: string;
  detail: unknown;
  status: 'pending' | 'applied' | 'rejected' | 'withdrawn';
  created_at: string;
  settled_at: string | null;
  auto_applied: boolean;
  proposed_via: string | null;
  proposed_by_email: string | null;
  settled_by_email: string | null;
};

export type ClaudeAccess = {
  switches: ClaudeSwitchRow[];
  proposals: ClaudeProposalRow[];
  durations: number[];
  note: string;
};

export const api = {
  me: () => request<Me>('/api/me'),
  workspaces: () => request<{ workspaces: Workspace[] }>('/api/workspaces'),
  bases: (workspaceId: string) =>
    request<{ bases: Base[] }>(`/api/workspaces/${workspaceId}/bases`),
  tables: (baseId: string) => request<{ tables: TableSummary[] }>(`/api/bases/${baseId}/tables`),
  table: (id: string) => request<{ table: TableMeta }>(`/api/tables/${id}`),
  // Sort and filter go to the SERVER. A grid that sorted the loaded page in the
  // browser would be wrong the moment a table outgrew one page of 200, and it
  // would look right — so the view's rules travel with the request.
  records: (
    id: string, trash: boolean,
    view?: { sort?: SortRule[]; filter?: FilterRule[]; search?: string },
    /**
     * WHERE TO START — C2's pager. A cursor from a previous page's `nextCursor`,
     * walked with `after=` so the cost of a page does not grow with how deep it
     * is (PERF-5). Absent means the first page.
     */
    after?: string | null,
    /** How many rows. The pager asks for a screenful; the export asks for more. */
    limit = 200,
  ) => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (after) p.set('after', after);
    if (trash) p.set('trash', '1');
    if (view?.sort?.length) p.set('sort', JSON.stringify(view.sort));
    if (view?.filter?.length) p.set('filter', JSON.stringify(view.filter));
    // SEARCH GOES TO THE SERVER, like sort and filter and for the same reason:
    // it runs against a Postgres full-text index, so it is the whole table
    // rather than the 200 rows that happen to be loaded (C1c-12).
    if (view?.search?.trim()) p.set('q', view.search.trim());
    return request<RecordPage>(`/api/tables/${id}/records?${p.toString()}`);
  },
  /**
   * ONE row, by id (C2-3). Used when a link chip is clicked: the row is in
   * ANOTHER table, so the grid does not have it. Same wall as the chip's title.
   */
  record: (id: string, recordId: string) =>
    request<{ table: TableMeta; record: Record<string, unknown> }>(
      `/api/tables/${id}/records/${recordId}`),
  createRecord: (id: string, values: Record<string, unknown>) =>
    request<{ record: { id: string } }>(`/api/tables/${id}/records`, {
      method: 'POST',
      body: JSON.stringify(values),
    }),
  patchRecord: (id: string, recordId: string, values: Record<string, unknown>) =>
    request<{ updated: number }>(`/api/tables/${id}/records/${recordId}`, {
      method: 'PATCH',
      body: JSON.stringify(values),
    }),
  /**
   * MOVE ONE ROW IN THE MANUAL ORDER — C2-9.
   *
   * Exactly one destination: `to_top`, or the id of the row it should land
   * after. The server refuses both-or-neither rather than guessing, because a
   * row moved somewhere nobody chose is worse than a refusal.
   *
   * It is console.records:write and the row's own write wall, not a structure
   * change: reordering rows is an edit to a row, so it does not go through the
   * DDL service and it grants nobody anything new.
   */
  moveRecord: (
    id: string,
    recordId: string,
    to: { to_top: true } | { after_row_id: string },
  ) =>
    request<{ moved: number; position: string }>(
      `/api/tables/${id}/records/${recordId}/move`,
      { method: 'POST', body: JSON.stringify(to) },
    ),
  /**
   * How many rows the listing holds (C2-5). Its own request because the pager is
   * LOAD-MORE: the listing does not change while you walk it, so this is asked
   * once when the view changes rather than on every page.
   */
  recordCount: (
    id: string, trash: boolean,
    view?: { filter?: FilterRule[]; search?: string },
  ) => {
    const p = new URLSearchParams();
    if (trash) p.set('trash', '1');
    if (view?.filter?.length) p.set('filter', JSON.stringify(view.filter));
    if (view?.search?.trim()) p.set('q', view.search.trim());
    return request<RecordCount>(`/api/tables/${id}/records/count?${p.toString()}`);
  },
  /** The same read, with the query string already built — used by CSV export, which pages. */
  recordsRaw: (id: string, params: URLSearchParams) =>
    request<RecordPage>(`/api/tables/${id}/records?${params.toString()}`),

  // One field, one value, many rows — in one transaction on the server rather
  // than a loop here, so it applies to every row it may touch or to none.
  bulkEdit: (id: string, ids: string[], field: string, value: unknown) =>
    request<{ updated: number; asked: number }>(`/api/tables/${id}/records/bulk`, {
      method: 'POST', body: JSON.stringify({ ids, field, value }),
    }),
  trashRecord: (id: string, recordId: string) =>
    request<{ updated: number }>(`/api/tables/${id}/records/${recordId}/trash`, { method: 'POST' }),
  restoreRecord: (id: string, recordId: string) =>
    request<{ updated: number }>(`/api/tables/${id}/records/${recordId}/restore`, { method: 'POST' }),
  /* -----------------------------------------------------------------------
   * ATTACHMENTS (C2b-A). Three calls: list, add, trash. There is no delete.
   * --------------------------------------------------------------------- */
  attachments: (id: string, recordId: string) =>
    request<{ attachments: (AttachmentCell & { field_id: string })[] }>(
      `/api/tables/${id}/records/${recordId}/attachments`),
  /**
   * Attach a pasted link — or, from the file paths, ask and be told no.
   *
   * `kind: 'file'` IS SENT ON PURPOSE rather than the browser refusing locally.
   * The refusal is the server's sentence, so there is one place it is written
   * and the day C2b-D opens the door, the same call starts working without this
   * file changing. A refusal invented in the browser would have to be found and
   * deleted, and the way that goes wrong is that it is not.
   */
  attach: (id: string, recordId: string, body: { fieldId: string; url?: string; kind?: 'file'; title?: string }) =>
    request<{ attachment: AttachmentCell }>(`/api/tables/${id}/records/${recordId}/attachments`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  trashAttachment: (id: string, recordId: string, attId: string) =>
    request<{ updated: number }>(
      `/api/tables/${id}/records/${recordId}/attachments/${attId}/trash`, { method: 'POST' }),
  /* -----------------------------------------------------------------------
   * UPLOADS (C2b-D). Four calls, and the browser does the moving.
   *
   * THE BYTES NEVER GO THROUGH THE CONSOLE. upload-intent hands back a
   * presigned PUT that the browser sends the file to directly, and `complete`
   * then tells the server to read it back, sniff it and accept it. A console
   * that proxied uploads would be a console that falls over on a 250 MB file.
   *
   * THE WINDOW IS THE SERVER'S, NOT THE URL'S (A3-4). `completable_until` is
   * what the console will honour; the signed URL itself outlives it, and
   * `complete` refuses past it. Nothing in the browser enforces the window —
   * a clock in a browser is not a control.
   * --------------------------------------------------------------------- */
  uploadIntent: (
    id: string, recordId: string,
    body: { fieldId: string; name: string; size: number; mime: string; sha256: string },
  ) =>
    request<{ attachment_id: string; upload_url: string; completable_until: string }>(
      `/api/tables/${id}/records/${recordId}/attachments/upload-intent`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  completeUpload: (id: string, recordId: string, attId: string) =>
    request<{ attachment: AttachmentCell; sniffed_mime: string | null; checksum_verified: boolean }>(
      `/api/tables/${id}/records/${recordId}/attachments/${attId}/complete`, { method: 'POST' }),
  /**
   * A2-2: MINTED ON AN EXPLICIT ACTION AND NOWHERE ELSE.
   *
   * There is no listing anywhere in this client that carries a download URL for
   * an upload, and there is no place to put one: the server leaves `url` null
   * for an upload in every listing and in the panel payload. This is the only
   * call that produces one, it happens when somebody presses Download, and the
   * link it returns is good for sixty seconds.
   */
  mintDownload: (id: string, recordId: string, attId: string) =>
    request<{ url: string; expires_in_seconds: number; note: string }>(
      `/api/tables/${id}/records/${recordId}/attachments/${attId}/mint`, { method: 'POST' }),
  restoreAttachment: (id: string, recordId: string, attId: string) =>
    request<{ attachment: AttachmentCell | null; file_available: boolean; message: string }>(
      `/api/tables/${id}/records/${recordId}/attachments/${attId}/restore`, { method: 'POST' }),

  history: (id: string, recordId: string) =>
    request<{ history: HistoryRow[] }>(`/api/tables/${id}/records/${recordId}/history`),
  brain: (search: string) =>
    request<{ items: BrainItem[] }>(`/api/brain/content?limit=200&search=${encodeURIComponent(search)}`),
  describe: (kind: 'workspaces' | 'bases' | 'tables' | 'fields', id: string, patch: { name?: string; description?: string }) =>
    request<{ updated: number }>(`/api/${kind}/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  // The DDL service. Every one of these is a thin wrapper over a SECURITY
  // DEFINER function that checks the scope AND the Owner Admin role again, so a
  // hostile replacement of this file gets nowhere — see migration 0012 §I.
  createWorkspace: (b: { slug: string; name: string; description?: string }) =>
    request<{ id: string }>('/api/ddl/workspace', { method: 'POST', body: JSON.stringify(b) }),
  createBase: (b: { workspaceId: string; slug: string; name: string; description?: string; tenantId?: string }) =>
    request<{ id: string }>('/api/ddl/base', { method: 'POST', body: JSON.stringify(b) }),
  createTable: (b: { baseId: string; slug: string; name: string; description?: string }) =>
    request<{ id: string }>('/api/ddl/table', { method: 'POST', body: JSON.stringify(b) }),
  createField: (b: {
    tableId: string; slug: string; name: string; type: string;
    description?: string; position?: number;
    // C2-A. Only read when type === 'link'; the server branches on the same
    // field, so there is one shape here and one route there.
    targetTableId?: string; allowMultiple?: boolean;
    reciprocalSlug?: string; reciprocalName?: string;
    // C2b-A. Only read when type === 'attachment'.
    accept?: string[];
  }) => request<{ id: string }>('/api/ddl/field', { method: 'POST', body: JSON.stringify(b) }),
  /**
   * M2 (e): `isTrigger` is gone. Trigger-ness is DERIVED from the automations
   * that name an option (migration 0044), so there is nothing to set here and
   * the database refuses the old parameter rather than ignoring it.
   */
  addOption: (b: { fieldId: string; value: string; label: string; color?: string }) =>
    request<{ id: string }>('/api/ddl/option', { method: 'POST', body: JSON.stringify(b) }),
  archive: (b: { kind: 'workspace' | 'base' | 'table' | 'field'; id: string }) =>
    request<{ archived: boolean }>('/api/ddl/archive', { method: 'POST', body: JSON.stringify(b) }),

  // ---------------------------------------------------------------------
  // The field editor (C1c). Every one of these is a thin wrapper over a
  // SECURITY DEFINER function added in migration 0017, gated on the SAME
  // console.assert_ddl_allowed() the older DDL calls use — scope first, then
  // the Owner Admin role. No new scope, and no new database privilege: 0017
  // grants console_app EXECUTE and nothing else.
  // ---------------------------------------------------------------------

  /**
   * Format and default value, both set every time.
   *
   * `defaultValue: null` MEANS "no default" and is how one is cleared. A
   * partial-update shape would have left no way to say it, and a default that
   * cannot be removed is worse than one that cannot be set.
   */
  configureField: (b: { fieldId: string; format: string; defaultValue: unknown }) =>
    request<{ configured: boolean }>('/api/ddl/field/config', { method: 'POST', body: JSON.stringify(b) }),
  /** C2-3's 'limit to one record'. Per side, like Airtable's. */
  configureAttachment: (b: { fieldId: string; allowMultiple: boolean }) =>
    request<{ updated: number }>('/api/ddl/field/attachment-config', {
      method: 'POST', body: JSON.stringify(b),
    }),
  configureLink: (b: { fieldId: string; allowMultiple: boolean }) =>
    request<{ configured: boolean }>('/api/ddl/field/link-config', { method: 'POST', body: JSON.stringify(b) }),
  moveField: (b: { fieldId: string; position: number }) =>
    request<{ moved: boolean }>('/api/ddl/field/move', { method: 'POST', body: JSON.stringify(b) }),
  duplicateField: (b: { fieldId: string; slug: string; name: string; position?: number }) =>
    request<{ id: string }>('/api/ddl/field/duplicate', { method: 'POST', body: JSON.stringify(b) }),
  /** The other half of "Delete field": the field Trash's way back. */
  restore: (b: { kind: 'workspace' | 'base' | 'table' | 'field'; id: string }) =>
    request<{ restored: boolean }>('/api/ddl/restore', { method: 'POST', body: JSON.stringify(b) }),
  trashedFields: (tableId: string) =>
    request<{ fields: TrashedField[] }>(`/api/tables/${tableId}/trashed-fields`),

  /** Rename or recolour a choice. Never its VALUE — that is what every row stores. */
  updateOption: (b: { optionId: string; label?: string; color?: string }) =>
    request<{ updated: boolean }>('/api/ddl/option/update', { method: 'POST', body: JSON.stringify(b) }),
  retireOption: (optionId: string) =>
    request<{ ok: boolean }>('/api/ddl/option/retire', { method: 'POST', body: JSON.stringify({ optionId }) }),
  restoreOption: (optionId: string) =>
    request<{ ok: boolean }>('/api/ddl/option/restore', { method: 'POST', body: JSON.stringify({ optionId }) }),
  /** Drag to reorder, and Alphabetize — the same call with the ids sorted. */
  reorderOptions: (fieldId: string, optionIds: string[]) =>
    request<{ reordered: number }>('/api/ddl/option/reorder', {
      method: 'POST', body: JSON.stringify({ fieldId, optionIds }),
    }),
  /**
   * "Reassign and retire" — the bulk replace.
   *
   * Needs records:write AND schema:write, and the server does both halves in one
   * transaction: rewriting the rows is a DATA change that must pass through RLS
   * and the row triggers, and retiring the choice afterwards is a STRUCTURE one.
   */
  reassignOption: (fieldId: string, from: string, to: string) =>
    request<{ updated: number; retired: boolean }>(`/api/fields/${fieldId}/options/reassign`, {
      method: 'POST', body: JSON.stringify({ from, to }),
    }),

  /**
   * AUTOMATIONS — C2b-12.
   *
   * Every one of these needs console.automations:manage, on the READ as well as
   * the write. That is not an oversight about the C1b grant-panel precedent: the
   * events_read policy has asked for the scope since 0012, so a route that let a
   * caller without it through would answer 200 with an empty list, and "there
   * are none" and "you cannot see them" must not share a response.
   */
  automations: () => request<{ automations: Automation[]; kinds: TriggerKind[] }>('/api/automations'),
  deliveries: (id: string) =>
    request<{ deliveries: Delivery[] }>(`/api/automations/${id}/deliveries`),
  /** The ONE response that ever carries the signing secret. */
  createAutomation: (a: {
    tableId: string; name: string; triggerKind: string; targetUrl: string;
    optionIds?: string[]; fieldId?: string | null;
    includeValues?: boolean; optionId?: string | null;
  }) =>
    request<{ automation: Automation; secret: string }>('/api/automations', {
      method: 'POST', body: JSON.stringify(a),
    }),
  updateAutomation: (id: string, a: { name?: string; isActive?: boolean; includeValues?: boolean; targetUrl?: string }) =>
    request<{ automation: Automation }>(`/api/automations/${id}`, {
      method: 'PATCH', body: JSON.stringify(a),
    }),
  /** ARCHIVE, never delete — the outbox rows point at this row and are the record of what it sent. */
  archiveAutomation: (id: string) =>
    request<{ automation: Automation }>(`/api/automations/${id}/archive`, { method: 'POST' }),

  // Saved views. By RLS rather than by convention: since migration 0032 a
  // person reaches their own rows plus anything shared with a base they can read
  // this table in, and may WRITE only their own (Matt's ruling, Decision
  // recUvf7AKmW5b75eG). Sharing a view shares no rows — see viewVisibility().
  views: (tableId: string) => request<{ views: SavedView[] }>(`/api/tables/${tableId}/views`),
  /**
   * `visibility` is OPTIONAL AND ITS ABSENCE IS NOT 'private'. The route treats
   * an absent value as "leave it as it is", so a re-save from anywhere that does
   * not offer the toggle cannot silently un-share a shared view. Send it only
   * when the person actually chose.
   */
  saveView: (tableId: string, v: { name: string; sort: SortRule[]; filter: FilterRule[]; hidden: string[]; widths: Record<string, number>; row_height?: RowHeight; visibility?: 'private' | 'base' }) =>
    request<{ view: { id: string } }>(`/api/tables/${tableId}/views`, { method: 'POST', body: JSON.stringify(v) }),

  /**
   * DUPLICATE A ROW — C1c-14.
   *
   * The copy lands directly below the row it came from. `links_not_copied` is
   * how many linked rows this person cannot see and therefore did not travel;
   * the grid says so out loud rather than letting the copy be quietly thinner
   * than the original.
   */
  duplicateRecord: (id: string, recordId: string) =>
    request<{ record: { id: string }; position: string | null; links_not_copied: number }>(
      `/api/tables/${id}/records/${recordId}/duplicate`, { method: 'POST' }),

  /**
   * PER-PERSON SETTINGS — C2-13, REQ-1.
   *
   * The catalogue travels WITH the values, every time, because the panel is
   * rendered FROM it: a preference added by a migration appears as a control
   * without this file changing. Nothing here names a setting key.
   */
  settings: () => request<SettingsPayload>('/api/me/settings'),
  /**
   * MERGES. Send only what changed; send a key as null to put it back to its
   * default. Sending the whole object would let a panel that knows about six
   * settings silently erase a seventh it has never heard of.
   */
  saveSettings: (patch: Record<string, unknown>) =>
    request<{ settings: Settings }>('/api/me/settings', {
      method: 'PATCH', body: JSON.stringify(patch),
    }),
  /**
   * OWNER SETTINGS — Matt's ruling D-7, Decision reccRrHWWA2ZKl3vw.
   *
   * These need console.settings:manage, which sits on the owner_admin ceiling
   * and no other, so today exactly one person can carry it. Same shape as the
   * per-person settings above and for the same reason: the row travels with its
   * label, its help, its bounds and whether it may be written, and the panel is
   * rendered FROM that. Nothing here names a setting key.
   */
  ownerSettings: () => request<{ config: PlanConfigRow[] }>('/api/owner-settings'),
  /**
   * ALL OR NOTHING. The route writes the whole patch in one transaction, so a
   * patch whose second key is refused writes neither — which is why this sends
   * an object rather than one key per request.
   */
  saveOwnerSettings: (patch: Record<string, number>) =>
    request<{ config: PlanConfigRow[] }>('/api/owner-settings', {
      method: 'PATCH', body: JSON.stringify(patch),
    }),
  /**
   * THE CLAUDE ACCESS PANEL — M1-8, M1-9, M1-12.
   *
   * READING is not browser-only, on purpose: M1-8 says the MCP may read the
   * switches so Claude can say "that is switched off right now" rather than
   * failing opaquely. WRITING one is browser-only and is refused for any other
   * caller by requireBrowserSession, before the scope wall, so the refusal
   * names the caller kind rather than the scope.
   *
   * `on` and `expires_at` are COMPUTED BY THE DATABASE and are not derived here
   * from enabled_at. A second copy of that arithmetic in the browser would be a
   * second thing to be wrong, and it would be wrong in the direction of showing
   * a switch as open after it had closed.
   */
  claudeAccess: () => request<ClaudeAccess>('/api/claude-access'),
  setClaudeSwitch: (name: string, on: boolean, durationSeconds?: number) =>
    request<{ switches: ClaudeSwitchRow[]; durations: number[] }>(
      `/api/claude-access/switches/${encodeURIComponent(name)}`,
      { method: 'PATCH', body: JSON.stringify(
          durationSeconds === undefined ? { on } : { on, durationSeconds }) },
    ),
  settleProposal: (id: string, outcome: 'applied' | 'rejected' | 'withdrawn') =>
    request<{ proposal: ClaudeProposalRow }>(
      `/api/claude-access/proposals/${encodeURIComponent(id)}/${outcome}`, { method: 'POST' }),
  deleteView: (tableId: string, viewId: string) =>
    request<{ deleted: number }>(`/api/tables/${tableId}/views/${viewId}`, { method: 'DELETE' }),

  // Grants. These need console.users:manage, which C1 does not mint at the
  // browser door — the grid renders the list read-only and says so rather than
  // hiding it, because "who can see this" is a question worth being able to
  // answer even when you cannot change the answer.
  grants: (level: GrantLevel, objectId: string) =>
    request<{ grants: GrantRow[] }>(`/api/grants?level=${level}&objectId=${objectId}`),
  identities: () => request<{ identities: IdentityRow[] }>('/api/identities'),
  // M1-E. Everyone on an internal tenant, the Owner Admin and anyone turned off
  // included. A read only; browser only, like the scope routes.
  users: () => request<{ users: UserRow[] }>('/api/users'),
  // M1-E part 2 (migration 0051). Add a person, and turn one off or back on.
  // Browser only under users:manage; every refusal after that is the database's
  // own sentence, passed through.
  createUser: (u: { email: string; displayName: string; role: 'admin' | 'developer'; scopes: string[] }) =>
    request<{ user: UserRow }>('/api/users', { method: 'POST', body: JSON.stringify(u) }),
  turnUserOff: (id: string) =>
    request<{ changed: boolean }>(`/api/users/${id}/off`, { method: 'POST' }),
  turnUserOn: (id: string) =>
    request<{ changed: boolean }>(`/api/users/${id}/on`, { method: 'POST' }),
  createGrant: (g: {
    identityId?: string; role?: string; level: GrantLevel; objectId: string;
    canView?: boolean; canEdit?: boolean; canCreate?: boolean; canDelete?: boolean;
    canManage?: boolean; ownRowsOnly?: boolean; note?: string;
  }) => request<{ grant: { id: string } }>('/api/grants', { method: 'POST', body: JSON.stringify(g) }),
  revokeGrant: (id: string) =>
    request<{ revoked: number }>(`/api/grants/${id}/revoke`, { method: 'POST' }),
  // M1-11. A person's SCOPE allowance, which is a different question from a
  // grant: a grant says which objects, a scope says which KIND of thing may be
  // done at all. Browser only — the route refuses the internal key and any MCP
  // token before it looks at the scope (M1-13 item 2).
  identityScopes: (identityId: string) =>
    request<IdentityScopes>(`/api/identities/${identityId}/scopes`),
  grantScope: (identityId: string, scope: string) =>
    request<{ granted: string }>(`/api/identities/${identityId}/scopes`, {
      method: 'POST', body: JSON.stringify({ scope }),
    }),
  revokeScope: (identityId: string, scope: string) =>
    request<{ revoked: string }>(`/api/identities/${identityId}/scopes/revoke`, {
      method: 'POST', body: JSON.stringify({ scope }),
    }),
  setOwnRowsField: (tableId: string, fieldId: string | null) =>
    request<{ updated: number }>(`/api/tables/${tableId}/own-rows-field`, {
      method: 'POST', body: JSON.stringify({ fieldId }),
    }),
  /** Migration 0052. Browser only, users:manage, Owner Admin — the server and the database each refuse otherwise. */
  setShareTitles: (tableId: string, on: boolean) =>
    request<{ share_titles_console_wide: boolean; changed: boolean }>(`/api/tables/${tableId}/share-titles`, {
      method: 'POST', body: JSON.stringify({ on }),
    }),
  /** Migration 0052: the link picker's list — the target read with your permissions, plus shared titles where a link can take them. */
  linkOptions: (fieldId: string, q: string) =>
    request<LinkOptions>(`/api/fields/${fieldId}/link-options?q=${encodeURIComponent(q)}`),

  signOut: () => request<void>('/auth/signout', { method: 'POST' }),
};

export type GrantLevel = 'workspace' | 'base' | 'table';

export interface GrantRow {
  id: string;
  identity_id: string | null;
  role: string | null;
  level: GrantLevel;
  object_id: string;
  can_view: boolean; can_edit: boolean; can_create: boolean;
  can_delete: boolean; can_manage: boolean; own_rows_only: boolean;
  note: string;
  created_at: string;
  revoked_at: string | null;
  google_email: string | null;
  display_name: string | null;
}

export interface IdentityRow {
  id: string;
  google_email: string;
  display_name: string;
  role: string;
}

/**
 * M1-E. One person on the Users list — who EXISTS, not who may be a target.
 * `revoked_at` set means turned off; `scopes` is what they hold today.
 */
export interface UserRow extends IdentityRow {
  confirmed_at: string | null;
  revoked_at: string | null;
  scopes: string[];
}

/** One scope, and where a particular person stands against it (M1-11). */
export interface ScopeAllowance {
  scope: string;
  kind: string;
  description: string;
  is_mintable: boolean;
  reserved_reason: string;
  /** Whether this person's ROLE may ever hold it. Off means the toggle is not offered. */
  on_ceiling: boolean;
  /** Whether this person holds it today. */
  granted: boolean;
  granted_at: string | null;
  granted_by: string | null;
}

export interface IdentityScopes {
  identity: IdentityRow;
  scopes: ScopeAllowance[];
}

/**
 * WHAT A SCOPE MEANS, IN A SENTENCE A PERSON CAN ACT ON.
 *
 * console.scopes carries a description written for whoever reads the schema.
 * This is the other audience. The label is what appears beside the toggle; the
 * description under it is the catalogue's, so a scope added by a migration
 * still shows up with something true beside it and no deploy.
 *
 * TRIGGERS, NOT SUBSCRIPTIONS — Matt's ruling of 2026-09-06. The console's
 * automation rows are called subscriptions in the code and are shown to people
 * as triggers, everywhere a person reads them.
 */
export const SCOPE_LABELS: Record<string, { label: string; plain: string }> = {
  'console.records:read':       { label: 'Read rows',      plain: 'See the rows in tables they have been given.' },
  'console.records:write':      { label: 'Edit rows',      plain: 'Add, change and move rows in those same tables.' },
  'console.schema:read':        { label: 'See structure',  plain: 'See what the columns are and how they are set up.' },
  'console.schema:write':       { label: 'Change structure', plain: 'Add and reshape columns — which changes the table for everyone using it.' },
  'console.brain:read':         { label: 'Read the brain', plain: 'Read the knowledge base behind the console.' },
  'console.brain:write':        { label: 'Write the brain', plain: 'Reserved. Nothing issues this.' },
  'console.automations:manage': { label: 'Manage triggers', plain: 'Set up and change the triggers that send rows out of this console.' },
  'console.users:manage':       { label: 'Hand out access', plain: 'Decide who may see what, and hold this screen.' },
  'console.credentials:manage': { label: 'The credential vault', plain: 'Reserved behind a one-time security review. Nobody holds it.' },
  'console.readlog:view':       { label: 'See the read log', plain: 'See who has looked at what.' },
  'console.settings:manage':    { label: 'Console settings', plain: 'Change what this console is set to, for everybody.' },
};

/**
 * The five permissions, in the order they escalate.
 *
 * `can_manage` is last and is not a bigger `can_edit` — it is the permission to
 * hand out permissions on that object, which is why it is worth its own column
 * and its own line in the UI.
 */
export const PERMISSIONS = [
  { key: 'canView', column: 'can_view', label: 'View', hint: 'See the object and read its rows.' },
  { key: 'canEdit', column: 'can_edit', label: 'Edit', hint: 'Change values in existing rows.' },
  { key: 'canCreate', column: 'can_create', label: 'Create', hint: 'Add new rows.' },
  { key: 'canDelete', column: 'can_delete', label: 'Trash', hint: 'Move rows to the trash, and restore them.' },
  { key: 'canManage', column: 'can_manage', label: 'Manage', hint: 'Give other people access to this object.' },
] as const;

export interface SortRule { field: string; dir: 'asc' | 'desc' }
export type FilterOp = 'is' | 'is_not' | 'contains' | 'is_empty' | 'is_not_empty' | 'gt' | 'lt';
export interface FilterRule { field: string; op: FilterOp; value: string }
/**
 * A saved view.
 *
 * `identity_id` is the OWNER and exists today. `visibility` does NOT exist yet —
 * this database is the foundation for a product where a view will be private or
 * shared with the base, and that column arrives in a later migration (C1c,
 * 0015). It is optional here so the grid tolerates it the moment it appears
 * rather than needing a change, and `viewVisibility()` below is the ONE place
 * that decides what to do when it is absent.
 */
export type RowHeight = 'short' | 'medium' | 'tall';

/** The three heights, and the pixel each one is worth. One definition. */
export const ROW_HEIGHTS: { value: RowHeight; label: string; px: number }[] = [
  { value: 'short', label: 'Short', px: 32 },
  { value: 'medium', label: 'Medium', px: 56 },
  { value: 'tall', label: 'Tall', px: 88 },
];

export function rowHeightPx(h: RowHeight | undefined): number {
  return ROW_HEIGHTS.find((r) => r.value === h)?.px ?? 32;
}

export interface SavedView {
  id: string;
  identity_id?: string;
  name: string;
  sort: SortRule[];
  filter: FilterRule[];
  hidden_fields: string[];
  widths: Record<string, number>;
  is_default: boolean;
  visibility?: 'private' | 'base';
  /** C1c-10, migration 0031. Absent on a view saved before that migration. */
  row_height?: RowHeight;
}

/**
 * ONE SETTING, AS THE CATALOGUE DESCRIBES IT — C2-13.
 *
 * This mirrors console.setting_keys and it is NOT a second copy of the list:
 * there is no list here, only the shape of a row. The keys, their bounds and
 * their words all arrive from the database, which is what makes adding a
 * preference a migration rather than a deploy. `kind` is what tells the panel
 * which control to draw.
 */
export interface SettingKey {
  key: string;
  kind: 'enum' | 'int' | 'bool' | 'timezone';
  allowed: string[] | null;
  min_int: number | null;
  max_int: number | null;
  default_value: unknown;
  label: string;
  help: string;
}

/**
 * A row of console.plan_config — the console's own configuration, as opposed to
 * one person's preferences.
 *
 * `is_owner_editable` decides whether a control appears or the value is shown
 * read-only, and `min_int`/`max_int` are the control's bounds. All three come
 * from the row, so flagging a second setting editable is a migration and this
 * file does not change. THE BOUNDS HERE ARE NOT THE WALL — the table's own
 * constraint is, and the route's sentence is what a person reads.
 */
export interface PlanConfigRow {
  key: string;
  value: unknown;
  label: string;
  help: string;
  is_owner_editable: boolean;
  min_int: string | number | null;
  max_int: string | number | null;
}

/**
 * WHAT IS WRONG WITH A TYPED-IN SETTING, or null when nothing is.
 *
 * IT TAKES THE RAW STRING, and that is the whole point of it existing rather
 * than living inside the panel as a number check. Matt, 2026-09-05: "I'm not
 * able to edit any." The box could not be EMPTIED — its onChange refused any
 * value that did not parse, so React restored the old one and Backspace did
 * nothing. A person clicks, selects all, hits delete, sees the number sitting
 * there unchanged, and correctly concludes the field is read-only.
 *
 * So a half-typed value is a normal state that the draft must be allowed to
 * hold, and "is this acceptable yet" is a separate question asked here. The
 * DATABASE is still the wall — plan_config_value_within_bounds refuses 0 and
 * 40 GB whatever this returns — and the route still answers with its own
 * sentence. This exists so the answer arrives while somebody is typing rather
 * than after they press Save.
 */
export function ownerSettingProblem(raw: string, min: number, max: number): string | null {
  const t = raw.trim();
  if (t === '') return `This needs a whole number between ${min.toLocaleString('en-US')} and ${max.toLocaleString('en-US')}.`;
  // The control is <input type="number">, which already keeps most rubbish out
  // — but not everywhere, and not from a paste. A digits-only test rather than
  // Number(): '1e9' and '0x10' both parse and neither is a number anybody typed
  // on purpose into a byte count.
  if (!/^\d+$/.test(t)) return 'Whole numbers only — no decimals, no separators, no minus sign.';
  const n = Number(t);
  if (!Number.isSafeInteger(n)) return 'That number is too large for this console to handle exactly.';
  if (n < min || n > max) {
    return `That has to be between ${min.toLocaleString('en-US')} and ${max.toLocaleString('en-US')}.`;
  }
  return null;
}

export type Settings = Record<string, unknown>;

export interface SettingsPayload {
  catalogue: SettingKey[];
  settings: Settings;
}

/**
 * What a setting is worth right now: the person's value if they have one, and
 * the catalogue's default otherwise.
 *
 * THE DEFAULT COMES FROM THE CATALOGUE AND NEVER FROM A CONSTANT IN THIS FILE.
 * A fallback literal here would be a second copy of the default that drifts the
 * first time a migration changes one, and it would drift silently — the panel
 * would show one number and the database would enforce another.
 */
export function settingValue<T>(p: SettingsPayload | null, key: string, ifAbsent: T): T {
  if (!p) return ifAbsent;
  if (key in p.settings) return p.settings[key] as T;
  const spec = p.catalogue.find((c) => c.key === key);
  return spec ? (spec.default_value as T) : ifAbsent;
}

/**
 * Private until the column says otherwise.
 *
 * IT NOW TELLS THE TRUTH FROM THE DATA. Migration 0032 made `visibility` live
 * (Matt's ruling, Decision recUvf7AKmW5b75eG option 1): 'base' means the view is
 * in the list of everybody who can READ this table, and only its owner may
 * rename, re-save, re-sort or delete it. Defaulting to private is still the safe
 * direction, and it is what a view saved before 0031 carries.
 *
 * WHAT SHARING DOES NOT DO, because it is the question anybody sensible asks
 * first: it shares no rows. A view is a sort, a filter, a hidden-column list,
 * widths and a row height. Opening a shared view runs the ordinary listing, so
 * each person still sees only the records their own grants admit — two people
 * can open the same view and see different numbers of rows, and that is correct.
 */
export function viewVisibility(v: SavedView): 'private' | 'base' {
  return v.visibility ?? 'private';
}

export const FILTER_OPS: { op: FilterOp; label: string; needsValue: boolean }[] = [
  { op: 'is', label: 'is', needsValue: true },
  { op: 'is_not', label: 'is not', needsValue: true },
  { op: 'contains', label: 'contains', needsValue: true },
  { op: 'gt', label: 'is greater than', needsValue: true },
  { op: 'lt', label: 'is less than', needsValue: true },
  { op: 'is_empty', label: 'is empty', needsValue: false },
  { op: 'is_not_empty', label: 'is not empty', needsValue: false },
];

/**
 * The field types the console can actually build.
 *
 * `ai` exists in the database enum and is refused by
 * `console.assert_buildable` with a message naming the phase that owns them.
 * They are deliberately absent here rather than shown-and-disabled: an option a
 * person can see and not use is a question they have to ask somebody.
 *
 * `link` JOINED THE LIST IN C2-A. It is the one type that needs more than a name
 * and a type to create — it needs a target table — so AddFieldDialog grows a
 * second step for it rather than every type growing an unused field.
 *
 * `attachment` JOINED IT IN C2b-A, and needs nothing extra at creation: a cell
 * holds links today and will hold files after C2b-D, and neither is a choice
 * made when the column is made. Its one setting, 'allow multiple', defaults to
 * true and lives in the field editor with the link field's equivalent.
 *
 * `rich_text` JOINED IT IN C2b-B, and like `link` and `attachment` it goes
 * through its own DDL function rather than console.add_field() — it owns TWO
 * columns, the ProseMirror document and the Markdown shadow the server generates
 * from it, and add_field() adds one. Nothing extra is chosen at creation.
 */
export const BUILDABLE_FIELD_TYPES = [
  { type: 'text', label: 'Text', icon: 'A' },
  { type: 'long_text', label: 'Long text', icon: '¶' },
  { type: 'number', label: 'Number', icon: '#' },
  { type: 'checkbox', label: 'Checkbox', icon: '☑' },
  { type: 'date', label: 'Date', icon: '📅' },
  { type: 'datetime', label: 'Date and time', icon: '🕘' },
  { type: 'single_select', label: 'Single select', icon: '◉' },
  { type: 'multi_select', label: 'Multi select', icon: '☰' },
  { type: 'status', label: 'Status', icon: '◍' },
  { type: 'link', label: 'Link to another record', icon: '🔗' },
  { type: 'attachment', label: 'Attachment', icon: '📎' },
  { type: 'rich_text', label: 'Rich text', icon: '¶+' },
] as const;

/**
 * The icon shown in a column header (C1c-1, Amendment 1).
 *
 * Falls back to a dot rather than to nothing: a type with no icon is a type this
 * file has not heard of, and an empty space says "text" to a reader.
 */
export function typeIcon(type: string): string {
  return BUILDABLE_FIELD_TYPES.find((t) => t.type === type)?.icon ?? '•';
}

export function typeLabel(type: string): string {
  return BUILDABLE_FIELD_TYPES.find((t) => t.type === type)?.label ?? type.replace(/_/g, ' ');
}

/**
 * FORMATS — how a value is shown and validated INSIDE its type.
 *
 * A format is not a type: changing one never touches the column, so it is
 * reversible, which is exactly why rating and duration are formats in RV-7
 * rather than types. The list is duplicated from the `fields_format_valid`
 * CHECK constraint in migration 0017 so the editor can offer the right options;
 * the database is what enforces it, for the MCP and the importer too.
 */
export const FORMATS: Record<string, { value: string; label: string; hint: string }[]> = {
  text: [
    { value: '', label: 'Plain text', hint: '' },
    { value: 'email', label: 'Email', hint: 'Shown as a mailto link.' },
    { value: 'url', label: 'URL', hint: 'Shown as a link.' },
    { value: 'phone', label: 'Phone', hint: 'Shown as a tel link.' },
  ],
  long_text: [
    { value: '', label: 'Plain text', hint: '' },
    { value: 'email', label: 'Email', hint: 'Shown as a mailto link.' },
    { value: 'url', label: 'URL', hint: 'Shown as a link.' },
    { value: 'phone', label: 'Phone', hint: 'Shown as a tel link.' },
  ],
  number: [
    { value: '', label: 'Plain number', hint: '' },
    { value: 'currency', label: 'Currency', hint: 'Shown as US dollars.' },
    { value: 'percent', label: 'Percent', hint: 'Shown with a % sign. 12 means 12%, not 1200%.' },
  ],
};

/** Whether a field type can carry a per-field default at all. */
export function canHaveDefault(type: string): boolean {
  // A checkbox column is `not null default false` and can never arrive NULL, so
  // the trigger that applies defaults can never see one — offering a default
  // that silently does nothing is worse than not offering it. Migration 0017
  // says the same thing in the same words.
  // A RICH TEXT FIELD HAS NO DEFAULT EITHER, and this one matters more than the
  // checkbox's. `console.fields.default_value` is jsonb and the row trigger
  // merges it straight into the column, so a default here would put an
  // UNVALIDATED value into the document column — past the schema check, past the
  // attachment rule, and with no Markdown shadow to match it. The database
  // refuses it too (migration 0034, `fields_rich_text_has_no_default`); this is
  // the half a person can see.
  return type !== 'checkbox' && type !== 'rich_text';
}

/**
 * THE CHOICE PALETTE — a fixed set of named tokens, not free-form colours.
 *
 * Tokens rather than hex, because these become tenant-brandable later (spec
 * C1c-2) and a database full of `#7c3aed` is a database that cannot be
 * rebranded. The value stored in console.field_options.color is the token; what
 * it looks like is this file's business and, one day, a tenant's.
 */
export const CHOICE_COLORS = [
  'grey', 'blue', 'teal', 'green', 'yellow', 'orange', 'red', 'pink', 'purple',
] as const;

export type ChoiceColor = (typeof CHOICE_COLORS)[number];

/** Unset, or a colour from a build that predates the palette, reads as grey. */
export function choiceColor(color: string): ChoiceColor {
  return (CHOICE_COLORS as readonly string[]).includes(color) ? (color as ChoiceColor) : 'grey';
}

/**
 * A ROW'S OWN URL (C1c-13).
 *
 * The point is not deep linking for its own sake: it is that a Matt Queue row,
 * or an agent, can say "open this row" and mean it. The id IS the link, and the
 * link is NOT an access token — opening it runs the same grant check as any
 * other read, so sending someone a URL grants them nothing.
 */
export function rowLink(
  parts: { workspace: string; base: string; table: string; rowId: string },
): string {
  return `/ws/${parts.workspace}/${parts.base}/${parts.table}/r/${parts.rowId}`;
}

/** The other direction: what /ws/a/b/c/r/<id> in the address bar means. */
export function parseRowLink(
  pathname: string,
): { workspace: string; base: string; table: string; rowId: string } | null {
  const m = /^\/ws\/([^/]+)\/([^/]+)\/([^/]+)\/r\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  return {
    workspace: decodeURIComponent(m[1]!), base: decodeURIComponent(m[2]!),
    table: decodeURIComponent(m[3]!), rowId: decodeURIComponent(m[4]!),
  };
}

/**
 * The slug rule, copied from `console.assert_slug` in migration 0012 so the
 * grid can say what is wrong BEFORE a round trip.
 *
 * It is a copy, and copies drift — so it is deliberately the LOOSER half of the
 * job: this only ever prevents a request the database would certainly refuse.
 * Every slug that passes here is still checked there, including the reserved
 * column names, which is why those are not repeated.
 */
export const SLUG_RE = /^[a-z][a-z0-9_]{0,45}$/;

/** A helpful default, not a rule — the person can always type over it. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'f$1')
    .slice(0, 46);
}

export interface Me {
  identityId: string;
  email: string;
  displayName: string;
  role: string;
  tenant: string;
  maxAccessTier: number;
  scopes: string[];
  kind: 'session' | 'internal_key';
}

export interface Workspace { id: string; slug: string; name: string; description: string; position: number }
export interface Base {
  id: string; slug: string; name: string; description: string; position: number;
  tenant_id: string | null; tenant_slug: string | null; pg_schema: string;
}
export interface TableSummary {
  id: string; slug: string; name: string; description: string; position: number;
  system_source: string | null;
}
export interface FieldOption {
  id: string;
  /** What every row stores. Renaming a choice never touches this. */
  value: string;
  /** What is on the screen. This is what a rename changes. */
  label: string;
  color: string;
  is_trigger: boolean;
  /**
   * Matt's ruling R2 option (b): a retired choice STAYS on the rows that carry
   * it, shows greyed, and cannot be assigned to a row that does not. So retired
   * options travel to the grid — an option it was never sent is one it would
   * render as a blank cell.
   */
  retired: boolean;
}

export interface Field {
  id: string; slug: string; name: string; description: string; type: string;
  pg_column: string; position: number;
  /** '' is plain. See FORMATS. */
  format: string;
  /** null is NO default, which is what every field arrives with. */
  default_value: unknown;
  options: FieldOption[];
  /* C2-A. All null unless type === 'link'. */
  link_target_table_id: string | null;
  link_reciprocal_field_id: string | null;
  link_allow_multiple: boolean | null;
  link_junction: string | null;
  link_is_source: boolean | null;
  /** Migration 0052: where the junction lives — `console_links` for a link across bases. */
  link_junction_schema?: string | null;
  /* C2b-A. Both null unless type === 'attachment'. */
  att_allow_multiple: boolean | null;
  att_accept: string[] | null;
}

/**
 * One linked row, as the server sends it (C2-2).
 *
 * `restricted` is NOT an error and NOT an empty cell. It is a row this person
 * may not read, shown as a chip with no title — Matt's ruling R4: count honest,
 * title withheld, never hidden. The grid must render it, because hiding it would
 * make the number of chips a lie.
 */
export interface LinkCell {
  id: string;
  title: string | null;
  restricted: boolean;
  /**
   * Migration 0052, CROSS-LINKS spec. Present (and true) only when this person
   * cannot read the row but its table shares titles console-wide. The chip shows
   * the title and does not open the row.
   */
  shared?: boolean;
}

/** What GET /api/fields/{id}/link-options returns (0052). `table` is null when the target is not yours to read. */
export interface LinkOptions {
  table: { id: string; name: string } | null;
  options: LinkOption[];
}
export interface LinkOption { id: string; title: string; shared?: boolean }

/**
 * C2b-A. What an attachment cell holds — the server's shape, unchanged.
 *
 * THERE IS NO `restricted` HERE AND THAT IS THE DESIGN, not an omission. A link
 * chip can be restricted because a link has a far side; an attachment belongs to
 * one record and to nothing else, so a caller who cannot read the record is sent
 * no attachment at all and absence leaks nothing (C2b-2).
 */
export interface AttachmentCell {
  id: string;
  source: 'upload' | 'embed' | 'external_ref';
  provider: string | null;
  providerName: string | null;
  icon: string;
  title: string;
  url: string | null;
  /** The iframe src for an embed, built by the server from the providers table. */
  embed: string | null;
  thumbnail: string | null;
  mime: string | null;
  size_bytes: number | null;
  scan_status: string;
}

export function attachmentCells(value: unknown): AttachmentCell[] {
  return Array.isArray(value) ? (value as AttachmentCell[]) : [];
}

/** A link cell is an array. Anything else is a field that has never been set. */
export function linkCells(value: unknown): LinkCell[] {
  return Array.isArray(value) ? (value as LinkCell[]) : [];
}

/**
 * THE IDS A CELL EDIT SENDS BACK — migration 0052.
 *
 * A restricted chip is never sent: the server keeps any link this person cannot
 * see, and leaving it out is how you avoid asking to delete it (C2-A).
 *
 * A SHARED chip is sent only where the server can take it — a link across bases,
 * written from its source side, which is the one place the database accepts a
 * link to a row this person cannot read. Everywhere else it is left out exactly
 * like a restricted one, and the server keeps it. Sending it there would be
 * refused, and the cell would look like it would not save.
 */
export function sharedLinkable(field: Pick<Field, 'link_junction_schema' | 'link_is_source'>): boolean {
  return field.link_junction_schema === 'console_links' && field.link_is_source === true;
}
export function keptLinkIds(field: Pick<Field, 'link_junction_schema' | 'link_is_source'>, cells: LinkCell[]): string[] {
  const linkable = sharedLinkable(field);
  return cells.filter((c) => !c.restricted && (!c.shared || linkable)).map((c) => c.id);
}

/** A field in the field Trash: hidden, its column and data intact, restorable. */
export interface TrashedField {
  id: string; slug: string; name: string; type: string; archived_at: string;
}
export interface TableMeta extends TableSummary {
  fields: Field[]; pg_schema: string; pg_table: string; base_id: string;
  /** Migration 0050: the field that decides whose row a row is, or null for "whoever created it". */
  own_rows_field_id?: string | null;
  /** Migration 0052: "Share titles console-wide". Read here; set only through setShareTitles(). */
  share_titles_console_wide?: boolean;
}
export interface RecordPage {
  table: TableMeta;
  records: Record<string, unknown>[];
  limit: number;
  offset: number;
  /**
   * The cursor for the NEXT page (PERF-5), or null at the end of the listing.
   *
   * Pass it back as `after=` instead of an offset to walk sequentially at O(page)
   * whatever the depth. THE GRID'S PAGER WALKS THIS (C2), and the streaming
   * export is built on the same predicate.
   *
   * IT IS BOUND TO THE LISTING SINCE EX-4 WAS CLOSED — the table and the
   * resolved sort are hashed into it — so a cursor minted before you changed the
   * sort is refused with a sentence instead of returning a page of the wrong
   * ordering. Changing the sort therefore starts again from page one, which is
   * what the pager does.
   */
  nextCursor: string | null;
  /**
   * How many rows the whole listing holds — after trash, search, every filter
   * and both permission walls.
   *
   * CAPPED. An exact count of production's 100,009-row table through the read
   * wall measured 9,035 ms, which is over the server's own 8-second statement
   * timeout: the pager would have failed while the page it pages worked. The
   * server counts to `countCap` and stops, so `totalIsExact: false` means "at
   * least this many" and the pager renders `10,000+`.
   *
   * (The total itself moved to its own route with the load-more pager — see
   * `RecordCount` below. This paragraph stays because it is the reason the
   * number has a `+` on it, and that is what a reader of the pager asks.)
   *
   * Whether a CHOSEN sort has an index yet — C2-6, Matt's ruling R7, WIDENED BY
   * PERF-10 because four of these six used to be spelled `'building'`.
   *
   * `null` — nothing was chosen; the default order has been indexed since 0019.
   * `'ready'` — indexed, and this is as fast as it gets.
   * `'building'` — slow, and the slowness is TEMPORARY: the server has asked for
   *   the index in the background and the table is inside the band it builds in.
   * `'not_needed'` — the table is under the 25,000-row floor. The sort is
   *   already fast, no index is coming, and there is NOTHING TO SAY: the grid
   *   renders no warning at all for this one. It is the common case and it used
   *   to render the 'building' promise.
   * `'too_large'` — over the 1,000,000-row ceiling. Permanent until the table
   *   shrinks: the console will not hold a write lock long enough to build it.
   * `'unindexable'` — a long text column is in the sort, which is never indexed
   *   for sorting; a single value can be bigger than a btree entry may be.
   * `'unindexed'` — the server asked and the build did not happen (refused,
   *   busy, or it failed). The sort works, exactly as it does now.
   * `'builtin_unindexed'` — a built-in column with no index is in the sort
   *   (today only Updated). Permanent, and nothing was refused: the console does
   *   not build indexes for the columns it owns rather than the ones you made.
   */
  sortIndex:
    | 'ready' | 'building' | 'unindexable' | 'too_large' | 'not_needed' | 'unindexed'
    | 'builtin_unindexed' | null;
}
/** GET /api/tables/{id}/records/count — the toolbar's "N of TOTAL" (C2-5). */
export interface RecordCount {
  /**
   * How many rows the whole listing holds — after trash, search, every filter
   * and both permission walls.
   *
   * CAPPED. An exact count of production's 100,009-row table through the read
   * wall measured 9,035 ms, which is over the server's own 8-second statement
   * timeout: the count would have failed while the page it counts worked. The
   * server counts to `countCap` and stops, so `totalIsExact: false` means "at
   * least this many" and the toolbar renders `10,000+`.
   */
  total: number;
  totalIsExact: boolean;
  countCap: number;
}
export interface HistoryRow {
  at: string; action: string; field_slug: string | null;
  old_value: unknown; new_value: unknown;
  by_email: string | null; by_name: string | null;
  /**
   * WHICH DOOR (migration 0016, audit #2 finding 7A). Both callers resolve to
   * the same identity — the mini's header key IS Matt and a browser session is
   * Matt too — so without these the history cannot tell them apart, and every
   * write Claude Code makes reads as Matt having made it. null means the write
   * did not come through the console service at all: a migration, the seed, or
   * an operator. That is a THIRD fact, not a missing one.
   */
  actor_kind: 'person' | 'internal_key' | 'mcp_client' | null;
  actor_label: string;
}
export interface BrainItem {
  id: string; brain_slug: string; title: string; summary: string; tags: string[];
  access_tier: number; audience: string; status: string;
  tenant_id: string | null; brand_id: string | null; updated_at: string;
}

/**
 * C2b-12. A webhook subscription, as the panel sees it.
 *
 * `secret` IS ABSENT FROM THIS TYPE ON PURPOSE. The server returns a signing key
 * exactly once — in the response that mints it — and never in a listing, so
 * there is no field here for one to land in. If a future route started sending
 * it, this type would not compile against the render that used it, which is a
 * cheaper alarm than a code review.
 */
/**
 * One kind of thing a subscription can fire on — M2 (e), migration 0044.
 *
 * SERVED, NOT HARD-CODED. The dropdown used to be ten <option> tags in this
 * file, which is the fourth of four places the same list of strings lived. It is
 * a table now (console.trigger_kinds), the route hands it over with the
 * listing, and a kind added by a future migration appears here without a deploy.
 */
export interface TriggerKind {
  key: string;
  label: string;
  description: string;
  /** The subscription must name a field — checkbox.set. */
  needs_field: boolean;
  /** The subscription must name at least one field option — choice.set. */
  needs_options: boolean;
  /** Which field types the picker may offer. Empty means it names no field. */
  field_types: string[];
}

export interface Automation {
  id: string;
  table_id: string;
  table_name: string;
  table_slug: string;
  name: string;
  trigger_kind: string;
  filter: Record<string, unknown>;
  target_url: string;
  is_active: boolean;
  include_values: boolean;
  option_ids: string[] | null;
  field_id: string | null;
  created_at: string;
  archived_at: string | null;
  pending: string;
  parked: string;
  delivered: string;
}

/** One attempt, as the delivery log renders it. Failures included — that is the point. */
export interface Delivery {
  id: string;
  attempt: number;
  at: string;
  status_code: number | null;
  ok: boolean;
  error: string;
  response: string;
  outbox_id: string;
  record_id: string | null;
  queued_at: string;
  done_at: string | null;
  parked_at: string | null;
  attempts: number;
  last_error: string;
  action: string;
  changed_fields: string[];
}

/**
 * C2b-D — UPLOADING ONE FILE, END TO END, FROM THE BROWSER.
 *
 * Three server calls with one direct PUT in between, and the PUT is the whole
 * architecture: the bytes go from this machine to the object store and never
 * touch the console. A console that proxied a 250 MB upload would hold a
 * request open for minutes and fall over on the second one.
 *
 * THE HASH IS COMPUTED HERE, BEFORE ANYTHING MOVES, and that is not an
 * optimisation. A3-6 fixes the object key as `b/<base>/<attachment>/<sha256>`,
 * so the server cannot name the object until it knows the hash. `complete` then
 * reads the bytes back and verifies it for anything under the plan's limit —
 * this value is a CLAIM until the server has checked it, which is why nothing
 * here treats it as evidence of anything.
 *
 * WHY XMLHttpRequest FOR THE PUT AND fetch() FOR EVERYTHING ELSE. `fetch` has no
 * upload progress event. At Matt's 250 MB cap a person watching a motionless
 * spinner for two minutes concludes it has hung and presses the button again,
 * which is C1c-4's shape — a thing that looks broken and is not. XHR reports
 * bytes sent, so the popover can say `62%`.
 *
 * EVERY FAILURE IS THE SERVER'S SENTENCE WHERE IT CAN BE. The refusals this can
 * hit — too large, wrong kind, not an internal identity, the window closed, the
 * bytes are a program — are all written once, on the server, and rendered here
 * verbatim. The only message invented in this file is the one for a PUT that
 * the object store itself refused, which the server never sees.
 */
export async function uploadOneFile(
  tableId: string,
  recordId: string,
  fieldId: string,
  f: File,
  onProgress?: (fraction: number) => void,
): Promise<AttachmentCell> {
  const digest = await crypto.subtle.digest('SHA-256', await f.arrayBuffer());
  const sha256 = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');

  const intent = await api.uploadIntent(tableId, recordId, {
    fieldId,
    name: f.name,
    size: f.size,
    // An empty type is what a browser reports for a file it does not recognise.
    // It is sent as-is: the server sniffs the bytes and overwrites this, and a
    // guess made here would be a second opinion nobody asked for.
    mime: f.type || 'application/octet-stream',
    sha256,
  });

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', intent.upload_url, true);
    if (f.type) xhr.setRequestHeader('content-type', f.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else {
        reject(new Error(
          `The file store refused the upload (${xhr.status}). Nothing was attached. ` +
          'Try again; if it keeps happening the console\'s storage credential may need attention.',
        ));
      }
    };
    xhr.onerror = () => reject(new Error(
      'The upload did not reach the file store. Check your connection and try again — nothing was attached.',
    ));
    xhr.onabort = () => reject(new Error('The upload was cancelled. Nothing was attached.'));
    xhr.send(f);
  });

  const done = await api.completeUpload(tableId, recordId, intent.attachment_id);
  return done.attachment;
}

export const SCOPE = {
  recordsRead: 'console.records:read',
  recordsWrite: 'console.records:write',
  schemaRead: 'console.schema:read',
  schemaWrite: 'console.schema:write',
  brainRead: 'console.brain:read',
  /**
   * C2b-C. Mintable since migration 0035 (R8, Decision recBqd0PVDTBbVJMd) and on
   * the owner_admin ceiling and no other, so today exactly one person holds it.
   * The panel asks for it before it renders a control, and says which fact it is
   * looking at when the answer is no.
   */
  automationsManage: 'console.automations:manage',
  /**
   * D-7. Mintable since migration 0042 (Decision reccRrHWWA2ZKl3vw) and on the
   * owner_admin ceiling and no other, so today exactly one person holds it. The
   * button asks for it before it renders, and the route asks for it again —
   * hiding a control is a courtesy, never a wall.
   */
  settingsManage: 'console.settings:manage',
  /**
   * RULING 3, migration 0015 (Decision recUcSGd1Quzsnj8g). Mintable, on the
   * owner_admin ceiling and no other. M1-11's screen asks for it before it
   * renders, the route asks for it again, and the RLS policies ask a third
   * time — hiding a control is a courtesy, never a wall.
   */
  usersManage: 'console.users:manage',
} as const;
